import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import type { SpecificResourceOverviewCardProps } from '@ctw/shared/content/overview/resource-overview-card';
import { withOverviewCardErrorBoundary } from '@ctw/shared/content/overview/with-overview-card-error-boundary';
import { useNavigateToRiskGapsV2 } from '@ctw/shared/content/patient-risk-gaps/helpers/risk-gaps-search-params';
import { buildRiskGapTelemetryContext } from '@ctw/shared/content/risk/utils';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import type { ReactNode } from 'react';
import {
  PatientRiskGapOverviewCard,
  type PatientRiskGapOverviewCardProps,
} from './patient-risk-gaps-overview-card';
import { usePatientRiskGapsOverviewData } from './use-patient-risk-gaps-overview-data';

export interface PatientRiskGapOverviewProps extends SpecificResourceOverviewCardProps {
  onAfterDetailsDrawerClosed?: () => void;
  RiskGapIdForDetailsDrawer?: string;
  onOpenDetailsDrawer?: (RiskGap: RiskGapModel) => void;
}

const NONE_FOUND_MESSAGE =
  'We didn’t find any suspect conditions or recapture opportunities for this patient';
const NONE_CURRENT_MESSAGE =
  'There are no active suspect conditions or recapture opportunities for this patient';

export const PatientRiskGapOverview = withOverviewCardErrorBoundary({
  cardTitle: 'Risk Capture',
  boundaryName: 'PatientRiskGapOverview',
  Component: ({ onSeeAllResources, onOpenDetailsDrawer }: PatientRiskGapOverviewProps) => {
    const workflowPreviewEnabled = useFeatureFlag('ui-riskcapture-workflow-preview');
    const telemetry = useTelemetry();
    const navigateToRiskGapV2 = useNavigateToRiskGapsV2();
    const { openResourceDrawer } = useResourceDrawer();
    const { data, isLoading, hasNoRecords } = usePatientRiskGapsOverviewData();

    const onRowClick: PatientRiskGapOverviewCardProps['onRowClick'] = workflowPreviewEnabled
      ? ({ record }) => {
          telemetry.trackInteraction(
            'risk_gap_overview_card_row_click',
            buildRiskGapTelemetryContext(record),
          );
          navigateToRiskGapV2(record);
        }
      : ({ telemetry: rowTelemetry, record: clickedRecord }) => {
          onOpenDetailsDrawer?.(clickedRecord);
          openResourceDrawer({
            telemetry: rowTelemetry,
            telemetryContext: {
              riskGapType: clickedRecord.type,
              riskGapStatus: clickedRecord.status,
              riskGapSubstatuses: clickedRecord.substatuses,
              riskGapConfidence: clickedRecord.confidenceLevel,
            },
            model: clickedRecord,
          });
        };

    let emptyState: ReactNode;
    if (workflowPreviewEnabled) {
      emptyState = (
        <div className={tw`flex flex-col items-center justify-center gap-5`}>
          <div>{hasNoRecords ? NONE_FOUND_MESSAGE : NONE_CURRENT_MESSAGE}</div>
        </div>
      );
    } else if (hasNoRecords) {
      emptyState = NONE_FOUND_MESSAGE;
    } else {
      emptyState = (
        <div className={tw`flex flex-col items-center justify-center gap-5`}>
          <div>{NONE_CURRENT_MESSAGE}</div>
        </div>
      );
    }

    return (
      <PatientRiskGapOverviewCard
        workflowPreviewEnabled={workflowPreviewEnabled}
        data={data}
        isLoading={isLoading}
        onRowClick={onRowClick}
        emptyState={emptyState}
        footerCTA={
          hasNoRecords
            ? undefined
            : {
                label: 'All Risk Capture',
                onClick: onSeeAllResources,
              }
        }
      />
    );
  },
});
